/**
 * DroneField — sprite pool for hangar mining drones. Drones are rendered as small
 * MTS_1 hulls (the Level-1 mothership), kept at the classic tiny drone size.
 */
import { Assets, Container, Sprite, Texture } from "pixi.js";
import type { DroneUnit } from "@/game/mining/DroneBay";
import { MINING_DRONE } from "@/config/balance";

export class DroneField {
  private container = new Container();
  private tex?: Texture;
  private baseScale = 1;
  private pool: Sprite[] = [];
  private active = new Map<number, Sprite>(); // drone id → sprite

  get view(): Container {
    return this.container;
  }

  async load(): Promise<void> {
    const base = import.meta.env.BASE_URL;
    this.tex = await Assets.load(`${base}sprites/MTS_1.png`);
    if (this.tex) {
      this.baseScale =
        MINING_DRONE.worldSize / Math.max(this.tex.width, this.tex.height);
    }
  }

  sync(units: DroneUnit[]): void {
    const present = new Set<number>();
    for (const u of units) {
      present.add(u.id);
      let s = this.active.get(u.id);
      if (!s) {
        s = this.pool.pop() ?? new Sprite();
        s.visible = true;
        s.anchor.set(0.5);
        if (this.tex) {
          s.texture = this.tex;
          s.scale.set(this.baseScale);
        }
        this.container.addChild(s);
        this.active.set(u.id, s);
      }
      s.position.set(u.pos.x, u.pos.y);
      // MTS_1 faces +Y (drill end); rotate so the nose follows the heading.
      s.rotation = u.heading - Math.PI / 2;
    }
    for (const [id, s] of this.active) {
      if (!present.has(id)) {
        s.visible = false;
        this.container.removeChild(s);
        this.pool.push(s);
        this.active.delete(id);
      }
    }
  }
}
